import React, { useState, Fragment } from 'react';
import { MagnifyingGlassIcon, CalendarIcon, FunnelIcon, CheckIcon, ChevronUpDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition, Listbox } from '@headlessui/react';

interface SurveillanceFilterFormProps {
  onSubmit: (filters: FilterFormData) => void;
  isLoading?: boolean;
}

interface FilterFormData {
  fromDate?: string;
  toDate?: string;
  status: 'all' | 'opened' | 'closed';
}

interface DateValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DateValidationModal: React.FC<DateValidationModalProps> = ({ isOpen, onClose, onConfirm }) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Dates invalides
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        La date de fin est antérieure à la date de début. Veuillez vérifier vos dates de recherche.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Voulez-vous continuer avec ces dates ou les corriger ?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={() => {
                      onClose();
                      onConfirm();
                    }}
                  >
                    Continuer quand même
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Corriger les dates
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// Status options configuration
const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les changements', description: 'Ouvertures et fermetures' },
  { value: 'opened', label: 'Ouvertures', description: 'Établissements passés à l\'état "Actif"' },
  { value: 'closed', label: 'Fermetures', description: 'Établissements passés à l\'état "Fermé"' }
];

const SurveillanceFilterForm: React.FC<SurveillanceFilterFormProps> = ({
  onSubmit,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<FilterFormData>({
    fromDate: '',
    toDate: '',
    status: 'all'
  });
  const [showDateValidationModal, setShowDateValidationModal] = useState(false);

  const validateDates = (fromDate: string, toDate: string): boolean => {
    if (!fromDate || !toDate) return true; // If either date is empty, no validation needed
    return new Date(fromDate) <= new Date(toDate);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check date validation
    if (formData.fromDate && formData.toDate && !validateDates(formData.fromDate, formData.toDate)) {
      setShowDateValidationModal(true);
      return;
    }
    
    onSubmit(formData);
  };

  const handleConfirmWithInvalidDates = () => {
    setShowDateValidationModal(false);
    onSubmit(formData);
  };

  const handleCancelDateValidation = () => {
    setShowDateValidationModal(false);
  };

  const handleInputChange = (field: keyof FilterFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatusChange = (value: 'all' | 'opened' | 'closed') => {
    setFormData(prev => ({
      ...prev,
      status: value
    }));
  };

  const handleReset = () => {
    const resetData = {
      fromDate: '',
      toDate: '',
      status: 'all' as const
    };
    setFormData(resetData);
    onSubmit(resetData);
  };

  const getSelectedStatusOption = () => {
    return STATUS_OPTIONS.find(option => option.value === formData.status) || STATUS_OPTIONS[0];
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date range filters */}
          <div>
            <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarIcon className="inline h-4 w-4 mr-1" />
              Date de début
            </label>
            <input
              type="date"
              id="fromDate"
              value={formData.fromDate}
              onChange={(e) => handleInputChange('fromDate', e.target.value)}
              className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>

          <div>
            <label htmlFor="toDate" className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarIcon className="inline h-4 w-4 mr-1" />
              Date de fin
            </label>
            <input
              type="date"
              id="toDate"
              value={formData.toDate}
              onChange={(e) => handleInputChange('toDate', e.target.value)}
              className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>

          {/* Enhanced Status filter */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              <FunnelIcon className="inline h-4 w-4 mr-1" />
              État administratif
            </label>
            <div className="relative">
              <Listbox value={formData.status} onChange={handleStatusChange}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-2 pl-10 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FunnelIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <span className="block truncate">
                      {getSelectedStatusOption().label}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {STATUS_OPTIONS.map((option) => (
                        <Listbox.Option
                          key={option.value}
                          className={({ active }) =>
                            `relative cursor-default select-none py-3 pl-10 pr-4 ${
                              active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                            }`
                          }
                          value={option.value}
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex flex-col">
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                                <span className="text-xs text-gray-500 mt-0.5">
                                  {option.description}
                                </span>
                              </div>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-end space-x-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                  Rechercher
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Help text */}
        <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-md">
          <p>
            <strong>Ouvertures :</strong> Établissements passés à l'état "Actif" (A)
          </p>
          <p>
            <strong>Fermetures :</strong> Établissements passés à l'état "Fermé" (F)
          </p>
          <p className="mt-1">
            La date correspond au moment où le changement d'état administratif a été enregistré.
          </p>
        </div>
      </form>

      {/* Date Validation Modal */}
      <DateValidationModal 
        isOpen={showDateValidationModal}
        onClose={handleCancelDateValidation}
        onConfirm={handleConfirmWithInvalidDates}
      />
    </>
  );
};

export default SurveillanceFilterForm; 